/**
 * The Newz Bridge entrance, as an explicit state machine.
 *
 * The single most important property here: **this machine never grants access.**
 * Authentication is authoritative and lives entirely in the auth context; this
 * only decides whether a decorative layer is drawn on top of an application
 * that is already mounted and already loading its data. If every line of this
 * file failed, the dashboard behind it would still be there and still be usable.
 *
 * That is why `SKIP` exists and why the watchdog uses it. A lost `transitionend`,
 * a backgrounded tab, a browser that refuses the animation — none of those may
 * leave somebody staring at a closed door.
 */

export const ENTRANCE_STATES = [
  /** Doors shut, login in front. Nothing behind them yet: there is no session. */
  'LOCKED',
  /** Credentials submitted, outcome unknown. The doors do not move on hope. */
  'AUTHENTICATING',
  /** Authenticated. The dashboard mounts behind the doors and starts loading. */
  'ACCESS_GRANTED',
  /** The lock releases and the edge lighting comes up. Nothing has moved yet. */
  'UNLOCKING',
  /** The panels are travelling. */
  'OPENING',
  /** Parked and docked. */
  'OPEN',
  /** Open without the journey, for someone who asked for less motion. */
  'REDUCED_MOTION_OPEN',
] as const;

export type EntranceState = (typeof ENTRANCE_STATES)[number];

export type EntranceEvent =
  | { type: 'AUTH_STARTED' }
  | { type: 'AUTH_FAILED' }
  | { type: 'AUTH_SUCCEEDED' }
  | { type: 'UNLOCK_RELEASED' }
  | { type: 'MOVEMENT_STARTED' }
  | { type: 'MOVEMENT_SETTLED' }
  /** Reduced motion, an animation that failed, or the watchdog. */
  | { type: 'SKIP' }
  /** A page load that already had a session. The Bridge was open before. */
  | { type: 'SESSION_RESTORED' }
  | { type: 'SIGNED_OUT' };

export interface EntranceOptions {
  reducedMotion: boolean;
}

/**
 * The mandated timing, in milliseconds from the moment authentication
 * succeeds. Exported as data so the tests and the stylesheet agree with the
 * specification rather than with each other.
 */
export const ENTRANCE_TIMELINE = {
  /** Login controls fade out. */
  loginFadeMs: 150,
  /** Lock releases; edge lighting comes up. Still no movement. */
  unlockAtMs: 100,
  /** Panels begin to travel, slowly. */
  movementAtMs: 200,
  /** Travel duration, from first movement to parked. */
  travelMs: 2500,
  /** Magnetic docking cue, just after the panels park. */
  dockAtMs: 2800,
  /** Total, auth success to fully open. */
  totalMs: 2800,
  /**
   * If the browser has not told us the movement finished by now, it never
   * will. Comfortably past the real duration, still short enough that nobody
   * decides the product is broken.
   */
  watchdogMs: 5000,
  /** Reduced motion: a fade, not a journey. */
  reducedMotionMs: 220,
} as const;

/** The easing the mandate asks for: heavy, engineered, no bounce. */
export const DOOR_EASING = 'cubic-bezier(0.65, 0, 0.35, 1)';

const OPENED: EntranceState[] = ['OPEN', 'REDUCED_MOTION_OPEN'];

export function isOpened(state: EntranceState): boolean {
  return OPENED.includes(state);
}

/** Whether the door layer is in the DOM at all. */
export function doorsPresent(state: EntranceState): boolean {
  return !isOpened(state);
}

/** Whether the login controls are visible. They fade the instant auth lands. */
export function loginVisible(state: EntranceState): boolean {
  return state === 'LOCKED' || state === 'AUTHENTICATING';
}

/**
 * Whether the panels still cover the application completely.
 *
 * Used to keep the dashboard out of the accessibility tree and out of the tab
 * order while it is hidden — it is mounted and loading, but nobody should be
 * able to tab into something they cannot see. The moment the panels start to
 * travel it becomes reachable again, because it is becoming visible.
 */
export function dashboardObscured(state: EntranceState): boolean {
  return (
    state === 'LOCKED' ||
    state === 'AUTHENTICATING' ||
    state === 'ACCESS_GRANTED' ||
    state === 'UNLOCKING'
  );
}

/**
 * Whether the panels should be drawn in their travelled position. Used by the
 * component to set a transform; the transition does the rest.
 */
export function panelsRetracted(state: EntranceState): boolean {
  return state === 'OPENING' || isOpened(state);
}

export function entranceReducer(
  state: EntranceState,
  event: EntranceEvent,
  options: EntranceOptions,
): EntranceState {
  // Signing out returns to a shut door from wherever we were, and does so
  // without ceremony. The security state has already changed by the time this
  // runs; the visual is catching up, not deciding.
  if (event.type === 'SIGNED_OUT') return 'LOCKED';

  // A page load that already had a session did not just authenticate. The
  // fiction is that the Bridge has been running all along, so replaying the
  // doors on every refresh would contradict it — and be tiresome.
  if (event.type === 'SESSION_RESTORED') {
    return options.reducedMotion ? 'REDUCED_MOTION_OPEN' : 'OPEN';
  }

  // Anything that cannot finish the journey ends it immediately and safely.
  if (event.type === 'SKIP') {
    if (state === 'LOCKED' || state === 'AUTHENTICATING') return state;
    return options.reducedMotion ? 'REDUCED_MOTION_OPEN' : 'OPEN';
  }

  switch (state) {
    case 'LOCKED':
      if (event.type === 'AUTH_STARTED') return 'AUTHENTICATING';
      // A door that opens on anything other than a completed sign-in is a
      // door that does not lock. Only the auth context emits this.
      if (event.type === 'AUTH_SUCCEEDED') return 'ACCESS_GRANTED';
      return state;

    case 'AUTHENTICATING':
      if (event.type === 'AUTH_SUCCEEDED') return 'ACCESS_GRANTED';
      // A refusal leaves the doors exactly where they were.
      if (event.type === 'AUTH_FAILED') return 'LOCKED';
      return state;

    case 'ACCESS_GRANTED':
      // Under reduced motion this state still happens, briefly: the dashboard
      // mounts behind the doors exactly as it otherwise would, and the caller
      // then sends SKIP after a short fade rather than starting the journey.
      // Keeping that timing out of here leaves this a pure transition table.
      if (event.type === 'UNLOCK_RELEASED') return 'UNLOCKING';
      return state;

    case 'UNLOCKING':
      if (event.type === 'MOVEMENT_STARTED') return 'OPENING';
      return state;

    case 'OPENING':
      if (event.type === 'MOVEMENT_SETTLED') return 'OPEN';
      return state;

    case 'OPEN':
    case 'REDUCED_MOTION_OPEN':
      // Already open. A second AUTH_SUCCEEDED — a re-render, a duplicated
      // effect — must not start the doors over.
      return state;

    default:
      return state;
  }
}

/**
 * The state a fresh page load starts in, given what the session turned out to
 * be. Kept here rather than in the component so the "refresh does not replay
 * the entrance" rule is testable without a browser.
 */
export function initialStateFor(
  authStatus: 'checking' | 'signed-in' | 'signed-out',
  options: EntranceOptions,
): EntranceState {
  if (authStatus === 'signed-in') {
    return options.reducedMotion ? 'REDUCED_MOTION_OPEN' : 'OPEN';
  }
  return 'LOCKED';
}
